import React, { useState, useEffect } from 'react';
import PageHeader from '../components/common/PageHeader';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Alert,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import { getGHGOverview, GHGOverview, GHGOverviewSortField, getCompanyGHG, CompanyGHG } from '../services/ghgReportingApi';
import { formatNumber } from '../utils/numberFormat';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import LicenseeFilter from '../components/common/LicenseeFilter';
import { CompanyStatusFilter } from '../services/api/companyApi';
import {
  buildTransportModeChartData,
  buildDateRangeOptions,
  formatEmissions,
} from './_shared/ghgStatsUtils';
import OrganizationalEmissionsCards from './_shared/OrganizationalEmissionsCards';
import TransportModeChartPair from './_shared/TransportModeChartPair';
import ExportButtonMenu from './_shared/ExportButtonMenu';
import PeriodFilterToggle from './_shared/PeriodFilterToggle';
import StatusFilterDropdown from './_shared/StatusFilterDropdown';
import GhgLoadingSkeleton from './_shared/GhgLoadingSkeleton';
import { useDateRangeFilter } from './_shared/useDateRangeFilter';
import { useGhgExportHandlers } from './_shared/useGhgExportHandlers';
import { GhgRankingSkeletonRow } from './_shared/RankingTableSkeleton';

const GHGOverviewPage: React.FC = () => {
  const { user } = useAuthStore();
  const [data, setData] = useState<GHGOverview | null>(null);
  const [companyData, setCompanyData] = useState<CompanyGHG | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const {
    filterMode, setFilterMode,
    selectedYear, setSelectedYear,
    fromDate, setFromDate,
    toDate, setToDate,
    availableYears, setAvailableYears,
    exporting, setExporting,
  } = useDateRangeFilter();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [rankingSortBy, setRankingSortBy] = useState<GHGOverviewSortField>('rank');
  const [rankingSortDir, setRankingSortDir] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<CompanyStatusFilter | null>(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');

  const isPlatformAdmin = user?.accountType === AccountType.MasterAdmin || user?.accountType === AccountType.CarbonCareAdmin;

  const { handleExcelExport, handlePdfExport } = useGhgExportHandlers({
    filterMode,
    fromDate,
    toDate,
    selectedYear,
    setExporting,
    setError,
    companyId: selectedCompanyId,
    statusFilter,
  });

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedCompanyId, fromDate, toDate, filterMode, rankingSortBy, rankingSortDir, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const options = buildDateRangeOptions(filterMode, fromDate, toDate, selectedYear);

      if (selectedCompanyId) {
        // Fetch specific company data
        const result = await getCompanyGHG(selectedCompanyId, options);
        setCompanyData(result);
        setData(null);
      } else {
        // Fetch overview data (all companies) — sort applied server-side
        const result = await getGHGOverview({
          ...options,
          sortBy: rankingSortBy,
          sortDir: rankingSortDir,
          statusFilter: statusFilter || undefined,
        });
        setData(result);
        setCompanyData(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load GHG reporting data');
    } finally {
      setLoading(false);
    }
  };

  if (!loading && error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Determine which data source to use
  const displayData = selectedCompanyId && companyData ? companyData.yearly : data?.aggregated;
  const organizationalLevel = displayData?.organizational_level;
  const transportChainElements = displayData?.transport_chain_elements;

  const hasData = Boolean(
    organizationalLevel ||
    transportChainElements ||
    (data && !selectedCompanyId && data.companies && data.companies.length > 0)
  );

  // Precompute bar chart data (raw values so recharts can auto-scale)
  const transportModeChartData = buildTransportModeChartData(
    transportChainElements as any,
  );

  // Only include modes with actual emissions so log scale works (log(0) is undefined)
  const tonsChartData = transportModeChartData.filter(d => d.TONS > 0);

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <PageHeader
          description={selectedCompanyName ? `GHG emissions report for ${selectedCompanyName}.` : 'Platform-wide GHG reporting data and summaries.'}
          actions={
            <ExportButtonMenu
              onRefresh={fetchData}
              onExcelExport={handleExcelExport}
              onPdfExport={handlePdfExport}
              exporting={exporting}
              showExport={isPlatformAdmin && hasData}
            />
          }
        />

        {/* Filters Bar */}
        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          {/* Company Selector (Master Admin only) */}
          {isPlatformAdmin && (
            <>
              <LicenseeFilter
                value={selectedCompanyId || ''}
                onChange={(id, name) => {
                  setSelectedCompanyId(id === '' ? null : Number(id));
                  setSelectedCompanyName(name);
                }}
                label="Licensee"
                sx={{ minWidth: 250, width: 'auto' }}
              />
              <Divider orientation="vertical" flexItem />
              <StatusFilterDropdown
                value={statusFilter}
                onChange={setStatusFilter}
                buttonHeight={54}
                buttonGap={1.5}
                menuItemPy={1.25}
              />
            </>
          )}

          {/* Period Selection + Date Selector */}
          <PeriodFilterToggle
            filterMode={filterMode}
            onFilterModeChange={setFilterMode}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            years={availableYears}
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
            yearSelectorSx={{ minWidth: 250 }}
          />
        </Paper>
      </Box>

      {!loading && !hasData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No data found for the selected company and year.
        </Alert>
      )}

      {/* Loading skeleton — organizational cards + transport-mode chart pair + ranking table */}
      {loading && (
        <>
          <GhgLoadingSkeleton />
          <Paper sx={{ p: 3 }}>
            <Skeleton variant="text" width={260} sx={{ fontSize: '1.25rem', mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Licensee</TableCell>
                    <TableCell align="right">TOT (Tonnes)</TableCell>
                    <TableCell align="right">OPS (Tonnes)</TableCell>
                    <TableCell align="right">ENE (Tonnes)</TableCell>
                    <TableCell align="right">EI (gr/tkm)</TableCell>
                    <TableCell align="right">TKM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 8 }, (_, i) => `rank-skeleton-${i}`).map((rowKey, idx) => (
                    <GhgRankingSkeletonRow key={rowKey} idx={idx} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Organizational Level Summary */}
      {!loading && organizationalLevel && (
        <OrganizationalEmissionsCards
          items={[
            { label: 'Total Emissions (TOT)', value: `${formatEmissions(organizationalLevel.tot_emissions)}`, unit: 'Tonnes', subtitle: '' },
            { label: 'Operational Emissions (OPS)', value: `${formatEmissions(organizationalLevel.ops_emissions)}`, unit: 'Tonnes', subtitle: '' },
            { label: 'Energy Provision (ENE)', value: `${formatEmissions(organizationalLevel.ene_emissions)}`, unit: 'Tonnes', subtitle: '' },
            { label: 'Emission Intensity (gr/tkm)', value: formatNumber(organizationalLevel.ei, 2), unit: 'gr/tkm', subtitle: '' },
            { label: 'TKM', value: formatNumber(organizationalLevel.tkm), unit: 'tkm', subtitle: 'Tonne-kilometers' },
          ]}
        />
      )}

      {/* Emissions by Transport Mode */}
      {!loading && transportModeChartData.length > 0 && (
        <TransportModeChartPair
          allData={transportModeChartData}
          tonsData={tonsChartData}
        />
      )}

      {/* Company Rankings - Only show when viewing all companies */}
      {!loading && data && !selectedCompanyId && data.companies && data.companies.length > 0 && (() => {
        const handleRankingSort = (field: GHGOverviewSortField) => {
          if (rankingSortBy === field) {
            setRankingSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
          } else {
            setRankingSortBy(field);
            // Sensible default direction per field type: asc for rank/name, desc for numbers.
            setRankingSortDir(field === 'rank' || field === 'company_name' ? 'asc' : 'desc');
          }
        };
        const rankingHeader = (field: GHGOverviewSortField, label: string) => (
          <TableSortLabel
            active={rankingSortBy === field}
            direction={rankingSortBy === field ? rankingSortDir : 'asc'}
            onClick={() => handleRankingSort(field)}
          >
            {label}
          </TableSortLabel>
        );

        // Backend returns companies pre-sorted by sortBy/sortDir, with a stable
        // canonical rank (computed from total_tot_emissions DESC) on each row.
        const sorted = data.companies;

        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Company Rankings by Emissions
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={rankingSortBy === 'rank' ? rankingSortDir : false}>
                      {rankingHeader('rank', 'Rank')}
                    </TableCell>
                    <TableCell sortDirection={rankingSortBy === 'company_name' ? rankingSortDir : false}>
                      {rankingHeader('company_name', 'Licensee')}
                    </TableCell>
                    <TableCell align="right" sortDirection={rankingSortBy === 'total_tot_emissions' ? rankingSortDir : false}>
                      {rankingHeader('total_tot_emissions', 'TOT (Tonnes)')}
                    </TableCell>
                    <TableCell align="right" sortDirection={rankingSortBy === 'total_ops_emissions' ? rankingSortDir : false}>
                      {rankingHeader('total_ops_emissions', 'OPS (Tonnes)')}
                    </TableCell>
                    <TableCell align="right" sortDirection={rankingSortBy === 'total_ene_emissions' ? rankingSortDir : false}>
                      {rankingHeader('total_ene_emissions', 'ENE (Tonnes)')}
                    </TableCell>
                    <TableCell align="right" sortDirection={rankingSortBy === 'total_ei' ? rankingSortDir : false}>
                      {rankingHeader('total_ei', 'EI (gr/tkm)')}
                    </TableCell>
                    <TableCell align="right" sortDirection={rankingSortBy === 'total_tkm' ? rankingSortDir : false}>
                      {rankingHeader('total_tkm', 'TKM')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.map((company) => (
                    <TableRow
                      key={company.company_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => globalThis.location.href = `/ghg-reporting/company/${company.company_id}`}
                    >
                      <TableCell>
                        <Chip
                          label={`#${company.rank}`}
                          size="small"
                          color={company.rank <= 3 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {company.company_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatEmissions(company.total_tot_emissions)}</TableCell>
                      <TableCell align="right">{formatEmissions(company.total_ops_emissions)}</TableCell>
                      <TableCell align="right">{formatEmissions(company.total_ene_emissions)}</TableCell>
                      <TableCell align="right">{formatNumber(company.total_ei, 2)}</TableCell>
                      <TableCell align="right">{formatNumber(company.total_tkm)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })()}
    </Box>
  );
};

export default GHGOverviewPage;
